<template>
  <c3-chart v-if="loaded" ref="chart" :height="height" :data="chartData" :config="chartConfig" type='area' />
  <v-skeleton-loader type="image" v-else :height="height" />
</template>

<script>
import C3Chart from '@/components/shared/chart/C3Chart.vue'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import Moment from 'moment-timezone'
import {map, get, round, min, max, some} from 'lodash'

export default {
  name: 'TemperatureViolationsChart',
  components: {
    C3Chart
  },
  mixins: [
    CurrentUserMixin
  ],
  props: {
    selectedCustomers: {
      type: Array,
      default () {
        return []
      }
    },
    height: {
      type: Number,
      default: 200
    }
  },
  data () {
    return {
      loaded: false,
      reportData: null
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    /**
     * Expose internal chart fit method, so we can redraw on demand
     */
    fit () {
      this.$refs.chart.fit()
    },
    /**
     * Load the chart data from the API
     */
    async loadData () {
      this.loaded = false
      try {
        const customerIds = map(this.selectedCustomers, x => x.id)
        const start = Moment.tz(this.customerTimezone).subtract(6, 'months').format()
        const end = Moment.tz(this.customerTimezone).format()
        const result = await this.$httpAdapter.get('/customer-insights/temperature-violation-history', {
          params: {
            customer_id: customerIds.join(','),
            start: start,
            end: end
          }
        })
        this.reportData = get(result.data, 'combined.data')
        this.reportData = this.filterData(this.reportData)
        this.loaded = true
      } catch (error) {
        this.handleError(error)
      }
    },
    /**
     * On days with no routes, we can't calculate proper percentages, so we reset the percentage of in spec routes to 0%
     * by default. This alarms some users, because it sticks out in the charts and appears to affect the primary score
     * metric (it doesn't in reality, unless there's no data for the *entire* period.)
     * Instead of showing 0%, we should show a gap in the data, since no data on a particular day is probably of
     * interest to the users. To do this, we just reset the display values to null where applicable.
     * @param data
     * @returns {Array}
     */
    filterData (data) {
      return map(data, x => {
        if (get(x, 'num_total_routes') === 0) {
          x.percent_routes_in_spec = null
        }
        return x
      })
    }
  },
  computed: {
    /**
     * Format the chart data needed for the graph.
     * @returns Object
     */
    chartData () {
      const dateColumn = map(this.reportData, x => x.date)
      dateColumn.unshift('date')
      const dataColumn = map(this.reportData, x => x.percent_routes_in_spec)
      dataColumn.unshift(this.$t('percentage_of_healthy_vehicles'))
      return {
        x: 'date',
        columns: [dateColumn, dataColumn]
      }
    },
    /**
     * Set the range for the chart. If any values fall below 60, show the full range, otherwise truncate to 60.
     * @returns {number[]}
     */
    rangeValues () {
      if (some(this.chartData.columns[1], x => x != null && x < 60)) {
        return [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]
      } else {
        return [60, 70, 80, 90, 100]
      }
    },
    /**
     * The chart configuration.
     * @returns Object
     */
    chartConfig () {
      return {
        padding: {
          bottom: -30,
          left: 40
        },
        point: {
          show: false
        },
        legend: {
          hide: true
        },
        colors: [
          '#195152'
        ],
        axis: {
          x: {
            type: 'timeseries',
            tick: {
              count: 12,
              format: '%Y-%m-%d',
              centered: true
            }
          },
          y: {
            label: {
              text: this.$t('percentage_of_routes_in_spec')
            },
            tick: {
              values: this.rangeValues,
              format: function (d) {
                return round(d, 1) + '%'
              },
              culling: true
            },
            min: min(this.rangeValues),
            max: max(this.rangeValues)
          }
        },
        line: {
          connectNull: true
        }
      }
    }
  }
}
</script>

<style scoped>

</style>
